import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import T from 'i18n';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { useForm, useFieldArray } from 'react-hook-form';
import {
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import Button from '@mui/material/Button';
import {
  AddCard,
  Delete,
  ExpandMore,
  ExpandLess,
  Send,
  WarningAmber,
  Warning,
  Close,
  Info,
} from '@mui/icons-material';
import Split from 'react-split';
import { Col, Form, Row } from 'react-bootstrap';
import { TextForm } from 'features/common';
import { selectCurrentFolder, selectCurrentCaseId } from 'common/selectors';
import { useFetchNotifications } from 'features/case/redux/fetchNotifications';
import { Modal } from 'features/common/MaterialBasedComponents';
import MoveDocument from './MoveDocument';
import * as actions from '../../redux/actions';
import FileViewer from '../../FileViewer';
import api from 'common/api';
import { useToasts } from 'react-toast-notifications';
import { generateSplitFolderName } from '../utils';

// Module-level constant regex: Pattern to match TV-####-#### format (TV followed by 3-4 digits, hyphen, 3-4 digits)
// Exported in case other modules need to reuse the same validation logic.
export const TV_TICKET_ID_PATTERN = /^TV-\d{3,4}-\d{3,4}$/i;

const ModalSplit = ({ show, handleClose, selectedRows }) => {
  const scrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: transparent;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background-color: #cdcdcd;
      border-radius: 10px;
    }
  `;
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { transientNotifications } = useFetchNotifications();
  const file = selectedRows?.[selectedRows.length - 1];
  const currentFolder = useSelector(selectCurrentFolder);
  const currentCaseId = useSelector(selectCurrentCaseId);
  const caseState = useSelector((state) => state.case);
  const [formIsValid, setFormIsValid] = useState(false);
  const [isSplitting, setIsSplitting] = useState(false);
  const [baselineSplitCount, setBaselineSplitCount] = useState(0);
  const [splitFileCount, setSplitFileCount] = useState(0); // track number of files being split
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [hasAnalysed, setHasAnalysed] = useState(false);
  const [aiParsedFiles, setAiParsedFiles] = useState(null);
  const [hasAutoFilled, setHasAutoFilled] = useState(false);
  const [initialFileEntries, setInitialFileEntries] = useState(null);
  const [noSplitFound, setNoSplitFound] = useState(false);
  const [searchQuery, setSearchQuery] = useState(
    'Extract all entries from the Table of Contents that list a document/file and its page number(s).\n' +
      'Output **only** a single pipe-delimited table with these columns (in order): Section Name, File Name, Page Number, Citation.\n' +
      'Page Number format rules: each cell must be either a single positive integer (e.g. 5) or a hyphenated inclusive range (e.g. 4-9). The cell MUST contain only digits or digits-hyphen-digits. Do NOT include any prefixes like "Page", "Pages", "Pg", "P." or "page" before the number. Do NOT use commas, ampersands (&), slashes (/), periods, underscores, the word "Page", or any other separators/symbols. If a page number cannot be determined, leave the Page Number cell blank. Never output formats like Page1, page5, Pg7, P.9, 20&21, 3/4, 1,2,4-9.\n' +
      'Sort the rows strictly in ascending order by Page Number (using the start of the range for ranges). Rows with blank Page Number must appear at the end of the table. Maintain stability for identical page numbers.\n' +
      'Do not include any additional text, explanations, or commentary.\n' +
      'Include every relevant entry across the entire TOC, even if it spans multiple sections or blocks.',
  );

  const [isAccordionExpanded, setIsAccordionExpanded] = useState(false);
  const [showAnalysisMessage, setShowAnalysisMessage] = useState(false);
  const [activeOffsetIndex, setActiveOffsetIndex] = useState(null);
  const [offsetFromHereValue, setOffsetFromHereValue] = useState('');
  const [showAsyncInfo, setShowAsyncInfo] = useState(false);
  const [showPageInfoImage, setShowPageInfoImage] = useState(false);
  const [showZoomedImage, setShowZoomedImage] = useState(false);
  const [selectedFolderName, setSelectedFolderName] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);

  // Ref for FileViewer to access page navigation methods
  const fileViewerRef = useRef(null);

  const startPage = 1;
  const endPage = file?.lastPage && file?.startPage ? file.lastPage - file.startPage + 1 : '';
  const fileName = file?.name ? `${file.name} - Split 1` : '';

  const autoGeneratedFolderName = generateSplitFolderName(file);

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    watch,
  } = useForm({
    mode: 'all',
    defaultValues: {
      fileEntries: [
        {
          name: fileName,
          startPage: startPage,
          endPage: endPage,
        },
      ],
      folder: 'auto-generated',
    },
    reValidateMode: 'onChange',
  });
  const formValues = watch();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'fileEntries',
  });

  // Set initial baseline on mount if not already set (for default single-split case)
  useEffect(() => {
    if (!initialFileEntries) {
      setInitialFileEntries([
        {
          name: fileName,
          startPage: startPage,
          endPage: endPage,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handler to navigate to a specific page in the FileViewer
  const handleGoToPage = (pageNumber) => {
    if (fileViewerRef.current && fileViewerRef.current.goToPage) {
      fileViewerRef.current.goToPage(pageNumber);
    } else if (fileViewerRef.current && fileViewerRef.current.scrollToPage) {
      fileViewerRef.current.scrollToPage(pageNumber);
    }
  };

  const handleAddFileEntry = () => {
    // Create a name for the new entry with incremented split number
    const newSplitNumber = fields.length + 1;
    const newFileName = `${file?.name || ''} - Split ${newSplitNumber}`;

    // Auto-calculate startPage from previous group's endPage, if present
    const lastIndex = fields.length - 1;
    const previousEndPageRaw = formValues?.fileEntries?.[lastIndex]?.endPage;

    let autoStartPage = '';
    if (
      previousEndPageRaw !== undefined &&
      previousEndPageRaw !== null &&
      previousEndPageRaw !== '' &&
      !isNaN(parseInt(previousEndPageRaw, 10))
    ) {
      autoStartPage = parseInt(previousEndPageRaw, 10) + 1;
    }

    // Add a new entry with the auto-generated name and startPage
    append(
      {
        name: newFileName,
        startPage: autoStartPage,
        endPage: '',
      },
      {
        // Focus on the name field of the newly added entry
        shouldFocus: true,
      },
    );
  };

  const handleApplyOffsetFromHere = (startIndex) => {
    const offsetValue = parseInt(offsetFromHereValue, 10);
    if (isNaN(offsetValue)) {
      return;
    }

    // Use initialFileEntries if available, otherwise fall back to current form values
    const baseEntries = initialFileEntries || formValues.fileEntries;

    // Calculate max pages for current document (pages are 1-indexed in UI)
    const maxPages =
      file?.lastPage && file?.startPage ? file.lastPage - file.startPage + 1 : Infinity;

    // Apply offset only from startIndex to the end
    const updatedEntries = baseEntries.map((entry, index) => {
      // If index is before startIndex, keep original values unchanged
      if (index < startIndex) {
        return formValues.fileEntries[index];
      }

      // Apply offset to entries from startIndex onwards
      const initialStartPage = parseInt(entry.startPage, 10);
      const initialEndPage = parseInt(entry.endPage, 10);

      let newStart = isNaN(initialStartPage) ? entry.startPage : initialStartPage + offsetValue;
      let newEnd = isNaN(initialEndPage) ? entry.endPage : initialEndPage + offsetValue;

      // Clamp logic scoped to first and last entries only
      const isFirst = index === 0;
      const isLast = index === baseEntries.length - 1;
      if (isFirst && !isNaN(parseInt(newStart, 10)) && parseInt(newStart, 10) < 1) {
        newStart = 1;
      }
      if (isLast && !isNaN(parseInt(newEnd, 10)) && parseInt(newEnd, 10) > maxPages) {
        newEnd = maxPages;
      }
      if (isLast && !isNaN(parseInt(newEnd, 10)) && parseInt(newEnd, 10) < 1) {
        newEnd = 1;
      }

      // Ensure startPage does not exceed endPage after clamping
      if (
        !isNaN(parseInt(newStart, 10)) &&
        !isNaN(parseInt(newEnd, 10)) &&
        parseInt(newStart, 10) > parseInt(newEnd, 10)
      ) {
        newStart = newEnd;
      }

      return {
        ...entry,
        startPage: newStart,
        endPage: newEnd,
      };
    });

    // Clear and re-add all fields with updated values
    const currentFieldCount = fields.length;
    for (let i = currentFieldCount - 1; i >= 0; i--) {
      remove(i);
    }
    updatedEntries.forEach((entry) => {
      append(entry, { shouldFocus: false });
    });

    // Show success toast with the offset value
    const message =
      offsetValue === 0
        ? T.translate('generic.resetToInitialValues') || 'Reset to initial values'
        : T.translate('generic.offsetSuccessMessage', { offset: offsetValue });
    addToast(message, {
      appearance: 'success',
      autoDismiss: true,
      autoDismissTimeout: 5000,
    });

    // Reset offset input and hide the controls
    setOffsetFromHereValue('');
    setActiveOffsetIndex(null);
  };

  const handleOffsetFromHereKeyDown = (e, index) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyOffsetFromHere(index);
    }
  };

  const handleSubmitQuery = () => {
    if (!searchQuery || !searchQuery.trim()) {
      return;
    }
    // Clear existing form entries and reset state
    const currentFieldCount = fields.length;
    for (let i = currentFieldCount - 1; i >= 0; i--) {
      remove(i);
    }
    setAiParsedFiles(null);
    setHasAutoFilled(false);
    setInitialFileEntries(null);
    setNoSplitFound(false);

    // Perform new analysis with updated query
    performAIAnalysis(searchQuery);

    // Close the accordion after submitting
    setIsAccordionExpanded(false);
  };

  const handleQueryKeyDown = (e) => {
    // Submit on Enter, but allow Shift+Enter for new line
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitQuery();
    }
  };

  // First, store the maximum page value as a constant
  const maxDocumentPages =
    file?.lastPage && file?.startPage ? file.lastPage - file.startPage + 1 : Infinity;

  // Helper function to parse markdown table from rawAnswer
  const parseMarkdownTable = (rawAnswer) => {
    if (!rawAnswer || typeof rawAnswer !== 'string') {
      return [];
    }
    // Normalise lines (support \r\n) and drop empty ones
    const lines = rawAnswer
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    // Fallback: sometimes the model returns an entire table flattened into a single line
    // with '||' delimiters but no newlines. Detect and artificially split into tokens.
    if (lines.length === 0) return [];
    if (lines.length === 1 && /\|\|/.test(lines[0])) {
      const flat = lines[0];
      // Tokenize by double pipe; format chained calls for lint and clarity
      let tokens = flat
        .split('||')
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      if (tokens.length < 4) return []; // Need at least headers

      // Heuristic: Sometimes the model concatenates the last header with the first data value
      // e.g. 'Page Number||Citation Notice of Motion||...' -> we detect if any header token contains
      // two known header keywords. If so, we attempt to split off the tail after the header keyword.
      const KNOWN_HEADERS = ['section name', 'file name', 'page number', 'citation'];
      // Lower-case tokens for header detection but keep original for row values
      const lowerTokens = tokens.map((t) => t.toLowerCase());
      const headerEnd = 4; // expected header count
      // Fix combined header+value case inside first four tokens
      for (let i = 0; i < headerEnd; i++) {
        const lk = lowerTokens[i];
        if (!lk) continue;
        const matchedHeader = KNOWN_HEADERS.find((h) => lk.startsWith(h) && lk !== h);
        if (matchedHeader) {
          // Split original token into header + remainder
          const original = tokens[i];
          const remainder = original.substring(matchedHeader.length).trim();
          // Replace header token with clean header
          tokens[i] = matchedHeader;
          lowerTokens[i] = matchedHeader;
          // Insert remainder as first data token (just before tokens[headerEnd]) if non-empty
          if (remainder.length) {
            tokens.splice(headerEnd, 0, remainder);
            lowerTokens.splice(headerEnd, 0, remainder.toLowerCase());
          }
          break; // only fix first occurrence
        }
      }

      const headerTokens = tokens.slice(0, headerEnd).map((h) => h.toLowerCase());
      let sectionNameIndex = headerTokens.findIndex((h) => h.includes('section'));
      let fileNameIndex = headerTokens.findIndex((h) => h.includes('file'));
      let pageNumberIndex = headerTokens.findIndex((h) => h.includes('page'));
      // Allow 'pages'
      if (pageNumberIndex === -1) pageNumberIndex = headerTokens.findIndex((h) => h === 'pages');
      if (pageNumberIndex === -1) return []; // page number required

      const rowsTokens = tokens.slice(headerEnd);
      // Normalize rowsTokens: split any combined citation + next section/file token
      // Pattern: (TextN) <Next Section Title>
      const NORMALIZED = [];
      const citationComboRegex = /^\(Text\d+\)\s+.+/i;
      rowsTokens.forEach((tok) => {
        if (citationComboRegex.test(tok)) {
          const firstSpaceIdx = tok.indexOf(') '); // closing paren followed by space
          if (firstSpaceIdx !== -1) {
            const citationPart = tok.substring(0, firstSpaceIdx + 1).trim();
            const remainderPart = tok.substring(firstSpaceIdx + 2).trim();
            if (citationPart) NORMALIZED.push(citationPart);
            if (remainderPart) NORMALIZED.push(remainderPart);
            return;
          }
        }
        NORMALIZED.push(tok);
      });
      const rowsTokensNormalized = NORMALIZED;
      const rowWidth = headerEnd; // 4
      const parsedFiles = [];
      for (let i = 0; i < rowsTokensNormalized.length; i += rowWidth) {
        const row = rowsTokensNormalized.slice(i, i + rowWidth);
        if (row.length < rowWidth) break; // incomplete final row
        const sectionCell = sectionNameIndex !== -1 ? row[sectionNameIndex] : row[0];
        const fileCell = fileNameIndex !== -1 ? row[fileNameIndex] : sectionCell;
        let pageCell = pageNumberIndex !== -1 ? row[pageNumberIndex] : '';
        // Strip disallowed prefixes (case-insensitive) e.g. 'Page5', 'Pg7', 'P.9', 'Pages12'
        if (pageCell) {
          pageCell = pageCell.replace(/^(pages|page|pg|p\.)\s*/i, '');
        }
        // Parse page range or single
        let localStartPage = null;
        let localEndPage = null;
        if (pageCell) {
          // Reject comma-containing cells per new rules
          if (/,/.test(pageCell)) {
            // leave localStartPage/localEndPage null -> handled upstream (blank)
          } else {
            const rangeMatch = pageCell.match(/^(\d+)\s*[-–]\s*(\d+)$/);
            if (rangeMatch) {
              const s = parseInt(rangeMatch[1], 10);
              const e = parseInt(rangeMatch[2], 10);
              if (!isNaN(s) && !isNaN(e) && s > 0 && e >= s) {
                localStartPage = s;
                localEndPage = e;
              }
            } else {
              const single = parseInt(pageCell.replace(/[^0-9]/g, ''), 10);
              if (!isNaN(single) && single > 0) localStartPage = single;
            }
          }
        }
        if (fileCell && fileCell.toLowerCase() !== 'not specified') {
          parsedFiles.push({
            fileName: fileCell,
            startPage: localStartPage,
            endPage: localEndPage,
          });
        }
      }
      // Ensure client-side sort by startPage (nulls last)
      parsedFiles.sort((a, b) => {
        const aStart = a.startPage;
        const bStart = b.startPage;
        if (aStart == null && bStart == null) return 0;
        if (aStart == null) return 1; // a goes after
        if (bStart == null) return -1; // b goes after
        if (aStart !== bStart) return aStart - bStart;
        // Tie-breaker: keep original relative order (stable sort simulation) by not altering when equal
        return 0;
      });
      return parsedFiles;
    }

    // Header is always first line
    const headerLine = lines[0];

    // Determine if this is a double-pipe table (||) or single pipe. We also
    // support "simple" pipe-delimited text without leading/trailing pipes
    // and without a markdown separator row.
    const isDoublePipeDelimited = /\|\|/.test(headerLine);
    const delimiter = isDoublePipeDelimited ? '||' : '|';

    const splitColumns = (line) => {
      let cols = line.split(delimiter).map((c) => c.trim());
      // If using single pipe and the line began/ended with a pipe, we'll have
      // empty first/last tokens - remove them to keep indices aligned.
      if (!isDoublePipeDelimited) {
        if (cols.length && cols[0] === '') cols.shift();
        if (cols.length && cols[cols.length - 1] === '') cols.pop();
      }
      return cols; // keep empty middle cells (do NOT filter) so indices map to headers
    };

    const headerColsRaw = splitColumns(headerLine);
    const headers = headerColsRaw.map((h) => h.toLowerCase());

    // Identify column indices
    let sectionNameIndex = -1;
    let fileNameIndex = -1;
    let pageNumberIndex = -1;
    headers.forEach((h, idx) => {
      if (h.includes('section name') || h === 'section') sectionNameIndex = idx;
      if (h.includes('file name') || h === 'file') fileNameIndex = idx;
      if (h.includes('page number') || h === 'page' || h === 'pages') pageNumberIndex = idx;
    });

    if (pageNumberIndex === -1) {
      // Can't proceed without page numbers
      console.warn('parseMarkdownTable: page number column not found in header:', headerLine);
      return [];
    }

    // Helper: detect a markdown separator row (e.g. | --- | --- |) or generic
    const isSeparatorRow = (line) => /^[|:\-\s]+$/.test(line);

    // Determine first data row index.
    // Standard markdown: header, separator, then data. Simple tables: header then data.
    let dataStartIndex = 1; // assume simple (no separator)
    if (lines.length > 1 && isSeparatorRow(lines[1])) {
      dataStartIndex = 2; // skip separator line
    } else if (isDoublePipeDelimited) {
      // Previous logic expected no explicit separator when using ||
      dataStartIndex = 1;
    }

    const parsedFiles = [];

    for (let i = dataStartIndex; i < lines.length; i++) {
      const line = lines[i];
      if (!line) continue;
      if (isSeparatorRow(line)) continue; // skip stray separator-like lines
      if (line === headerLine) continue; // safety

      const columns = splitColumns(line);
      if (columns.length <= pageNumberIndex) continue; // not enough cols

      // Extract file/section names
      let extractedFileName = '';
      const candidateFileName = fileNameIndex !== -1 ? columns[fileNameIndex] : undefined;
      const candidateSectionName = sectionNameIndex !== -1 ? columns[sectionNameIndex] : undefined;
      if (candidateFileName) {
        if (
          typeof TV_TICKET_ID_PATTERN !== 'undefined' &&
          TV_TICKET_ID_PATTERN.test(candidateFileName)
        ) {
          extractedFileName = candidateSectionName || candidateFileName;
        } else {
          extractedFileName = candidateFileName;
        }
      } else if (candidateSectionName) {
        extractedFileName = candidateSectionName;
      }

      // Raw page cell from table
      let pageNumberStr = columns[pageNumberIndex];
      // Strip disallowed prefixes like 'Page', 'Pages', 'Pg', 'P.' directly concatenated or followed by space
      if (pageNumberStr) {
        pageNumberStr = pageNumberStr.replace(/^(pages|page|pg|p\.)\s*/i, '');
      }
      let parsedStartPage = null;
      let parsedEndPage = null;
      if (pageNumberStr) {
        // Ignore comma formats entirely
        if (/,/.test(pageNumberStr)) {
          // leave parsedStartPage / parsedEndPage null
        } else {
          const rangeMatch = pageNumberStr.match(/^(\d+)\s*[-–]\s*(\d+)$/); // support hyphen or en-dash
          if (rangeMatch) {
            const start = parseInt(rangeMatch[1], 10);
            const end = parseInt(rangeMatch[2], 10);
            if (!isNaN(start) && !isNaN(end) && start > 0 && end >= start) {
              parsedStartPage = start;
              parsedEndPage = end;
            }
          } else {
            const single = parseInt(pageNumberStr.replace(/[^0-9]/g, ''), 10);
            if (!isNaN(single) && single > 0) {
              parsedStartPage = single;
            }
          }
        }
      }

      if (extractedFileName && extractedFileName.toLowerCase() !== 'not specified') {
        parsedFiles.push({
          fileName: extractedFileName,
          startPage: parsedStartPage,
          // endPage null unless range provided; later logic infers if needed
          endPage: parsedEndPage,
        });
      }
    }

    // Final client-side sort for multi-line branch as well
    parsedFiles.sort((a, b) => {
      const aStart = a.startPage;
      const bStart = b.startPage;
      if (aStart == null && bStart == null) return 0;
      if (aStart == null) return 1;
      if (bStart == null) return -1;
      if (aStart !== bStart) return aStart - bStart;
      return 0;
    });
    return parsedFiles;
  };

  // Function to perform AI analysis (can be called on mount or when query changes)
  const performAIAnalysis = async (query) => {
    if (!file?.id || !file?.name || !currentCaseId || !query) {
      return;
    }

    setIsAnalysing(true);
    setShowAnalysisMessage(false);
    setHasAutoFilled(false);

    try {
      const matchesPayload = {
        search: query,
        filters: {
          folders: [],
          files: [
            {
              id: file.id,
              name: file.name,
            },
          ],
        },
        numMatches: 1000,
        multiQuery: false,
        useQueryRouter: false,
        source: 'split',
      };

      const matchesResponse = await api.post(
        `/cases/${currentCaseId}/matches?omitFiles=true`,
        matchesPayload,
      );

      // If status code indicates failure (non 2xx) treat as unexpected error
      if (
        matchesResponse?.status &&
        (matchesResponse.status < 200 || matchesResponse.status >= 300)
      ) {
        console.error('Unexpected matches API status:', matchesResponse.status);
        setShowAnalysisMessage(true);
        return;
      }

      // Check for errors
      if (matchesResponse?.error) {
        console.error('Error from fetchMatches API:', matchesResponse.error);
        setShowAnalysisMessage(true);
        return;
      }

      // Check if relevantFiles is empty or null
      if (!matchesResponse?.relevantFiles || matchesResponse.relevantFiles.length === 0) {
        addToast(T.translate('generic.splitFileMessage'), {
          appearance: 'warning',
          autoDismiss: true,
          autoDismissTimeout: 8000,
        });

        setShowAnalysisMessage(true);
        return;
      }

      // Extract searchHistoryId from the response
      const searchHistoryId = matchesResponse?.searchHistoryId;

      if (!searchHistoryId) {
        console.error('No searchHistoryId received from fetchMatches');
        setShowAnalysisMessage(true);
        return;
      }

      const answersPayload = {
        searchHistoryId: searchHistoryId,
        searchQuery: query,
        outputType: 'table',
      };

      const answersResponse = await api.post(`/cases/${currentCaseId}/answers`, answersPayload);

      if (
        answersResponse?.status &&
        (answersResponse.status < 200 || answersResponse.status >= 300)
      ) {
        console.error('Unexpected answers API status:', answersResponse.status);
        setShowAnalysisMessage(true);
        return;
      }

      // Check for errors
      if (answersResponse?.error) {
        console.error('Error from generateAnswers API:', answersResponse.error);
        setShowAnalysisMessage(true);
        return;
      }
      // Parse the rawAnswer to extract file names and page numbers
      if (answersResponse?.rawAnswer) {
        const parsedFiles = parseMarkdownTable(answersResponse.rawAnswer);
        if (parsedFiles.length > 0) {
          setAiParsedFiles(parsedFiles);
          setNoSplitFound(false);
        } else {
          setNoSplitFound(true);
        }
      }
    } catch (error) {
      console.error('Error during AI analysis:', error);
      setShowAnalysisMessage(true);
      return;
    } finally {
      // Only stop analysing here (don't force show message unless already set by error cases)
      setIsAnalysing(false);
    }
  };

  // useEffect to handle AI analysis when component loads
  useEffect(() => {
    if (!hasAnalysed) {
      setHasAnalysed(true);
      performAIAnalysis(searchQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect to auto-fill form fields when AI parsed files are ready
  useEffect(() => {
    if (!aiParsedFiles || aiParsedFiles.length === 0 || hasAutoFilled) {
      return;
    }

    // Calculate endPage for each file (if not already provided from range format)
    const fileEntriesData = aiParsedFiles.map((parsedFile, index) => {
      const isLastFile = index === aiParsedFiles.length - 1;
      let entryStartPage = parsedFile.startPage;
      let entryEndPage = parsedFile.endPage;

      // If startPage is null (page number not specified), leave both start and end blank
      if (entryStartPage === null) {
        entryStartPage = '';
        entryEndPage = '';
      } else if (entryEndPage !== null && entryEndPage !== undefined) {
        // endPage is already provided from range format (e.g., "4-6")
        // Keep both startPage and endPage as is
        entryStartPage = parsedFile.startPage;
        entryEndPage = parsedFile.endPage;
      } else {
        // Calculate endPage based on next file's startPage or document's last page
        if (isLastFile) {
          // Last file spans to document end
          entryEndPage = maxDocumentPages;
        } else {
          const nextStartPage = aiParsedFiles[index + 1].startPage;
          if (nextStartPage !== null) {
            // Infer end page as one before next start
            const inferred = nextStartPage - 1;
            // Clamp: if inferred < current start, treat as single-page segment
            if (entryStartPage !== null && inferred < entryStartPage) {
              entryEndPage = entryStartPage; // both same (single page)
            } else {
              entryEndPage = inferred;
            }
          } else {
            // Next file has no start page; we can't infer a proper end range
            entryEndPage = entryStartPage !== null ? entryStartPage : '';
          }
        }
      }

      return {
        name: parsedFile.fileName,
        startPage: entryStartPage,
        endPage: entryEndPage,
      };
    });

    // Store the initial values for offset calculations
    setInitialFileEntries(fileEntriesData);

    // Clear existing fields first (remove all but keep structure)
    const currentFieldCount = fields.length;
    for (let i = currentFieldCount - 1; i >= 0; i--) {
      remove(i);
    }

    // Add all the new entries without focusing/scrolling
    fileEntriesData.forEach((entry) => append(entry, { shouldFocus: false }));

    setHasAutoFilled(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiParsedFiles]);

  // Update the useEffect validation function
  useEffect(() => {
    const validateForm = () => {
      if (!formValues.fileEntries || !Array.isArray(formValues.fileEntries)) {
        setFormIsValid(false);
        return false;
      }

      // Check each file entry for required fields and validation errors
      const areEntriesValid = formValues.fileEntries.every((entry) => {
        // Check required fields
        const hasRequiredFields =
          entry &&
          entry.name &&
          entry.name.trim() !== '' &&
          entry.startPage &&
          entry.startPage.toString().trim() !== '' &&
          entry.endPage &&
          entry.endPage.toString().trim() !== '';

        if (!hasRequiredFields) return false;

        // Check for validation errors that would trigger custom error messages
        const startPageVal = parseInt(entry.startPage, 10);
        const endPageVal = parseInt(entry.endPage, 10);

        // Check if any page number is less than 1
        const hasNegativePageError =
          isNaN(startPageVal) || isNaN(endPageVal) || startPageVal < 1 || endPageVal < 1;

        // Check if start page is greater than end page
        const hasPageOrderError = startPageVal > endPageVal;

        // Check if end page exceeds document's total pages
        const hasExceedingPageError = endPageVal > maxDocumentPages;

        // Entry is valid only if there are no validation errors
        return !hasNegativePageError && !hasPageOrderError && !hasExceedingPageError;
      });

      // Folder selection is now optional, so we don't need to check for it
      const isValid = areEntriesValid;

      setFormIsValid(isValid);
      return isValid;
    };

    validateForm();
  }, [formValues, maxDocumentPages]);

  // Close modal and notify when split is completed via websocket notification
  useEffect(() => {
    if (!isSplitting) return;
    if (!Array.isArray(transientNotifications)) return;

    const splitNotifs = transientNotifications.filter((n) => n?.type === 'split');
    if (splitNotifs.length <= baselineSplitCount) return;

    const newestSplit = splitNotifs[splitNotifs.length - 1];
    if (newestSplit && newestSplit.transient === true && newestSplit.status === 'complete') {
      dispatch({
        type: 'CASE_FETCH_SIGNALR_NOTIFICATION_FEEDBACK',
        feedback: {
          error: newestSplit.status === 'failed',
          message: newestSplit.message,
        },
      });
      setIsSplitting(false);
      handleClose();
    }
  }, [isSplitting, transientNotifications, baselineSplitCount, handleClose, dispatch]);

  // Don't render if file is not available
  if (!file) {
    return null;
  }
  return (
    <>
      <style>{scrollbarStyles}</style>
      <Modal
        show={show}
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <span>{T.translate('generic.splitFile') + ' - ' + file.id}</span>
            <span
              style={{
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                position: 'absolute',
                left: '50%',
                transform: 'translateX(-50%)',
              }}
            >
              <Warning style={{ color: '#FFA500', fontSize: '1.5rem' }} />
              {T.translate('generic.splitBetaInfo')}
            </span>
          </div>
        }
        wide={true}
        handleClose={() => {
          handleClose();
        }}
        disableClose
        disableEscapeKeyDown={true}
        buttons={
          <>
            {showAsyncInfo ? (
              <Button type="button" variant="contained" color="primary" onClick={handleClose}>
                {T.translate('generic.ok') || 'Ok'}
              </Button>
            ) : (
              <>
                {isSplitting && (
                  <span
                    style={{
                      marginRight: '0.75rem',
                      fontWeight: 700,
                      color: '#4567e5',
                      fontSize: '1rem',
                    }}
                  >
                    {T.translate('generic.splitFileProgressCombined', { count: splitFileCount })}
                  </span>
                )}
              </>
            )}
          </>
        }
        {...(!showAsyncInfo && {
          handleSave: handleSubmit(async (data) => {
            const currentSplitCount = Array.isArray(transientNotifications)
              ? transientNotifications.filter((n) => n?.type === 'split').length
              : 0;
            setBaselineSplitCount(currentSplitCount);
            setIsSplitting(true);
            setSplitFileCount(data.fileEntries?.length || 0);
            const splitFiles = data.fileEntries.map((entry) => ({
              name: entry.name,
              startPage: parseInt(entry.startPage, 10),
              endPage: parseInt(entry.endPage, 10),
            }));

            try {
              let targetFolderId = data.folder || '';
              let usedFolderName = '';

              // If no folder is selected OR auto-generated folder is selected, create the folder
              if (!targetFolderId || targetFolderId === 'auto-generated') {
                const folderPayload = {
                  name: autoGeneratedFolderName,
                  type: 'team-bundle',
                };

                const addFolderResponse = await dispatch(
                  actions.addFolder(folderPayload, { suppressFeedback: true }),
                );

                // Check if folder creation was successful and get the folder ID
                if (addFolderResponse && addFolderResponse.id) {
                  targetFolderId = addFolderResponse.id;
                  usedFolderName = autoGeneratedFolderName;

                  // Step 2: Fetch the container to update the sidebar
                  try {
                    await dispatch(actions.fetchContainer({ folder: { type: 'team-bundle' } }));
                  } catch (containerError) {
                    console.error('Error fetching container:', containerError);
                    // Continue with split even if container fetch fails
                  }
                } else {
                  throw new Error('Failed to create folder');
                }
              } else {
                // Find the folder name from existing folders
                const allFolders = [
                  ...(caseState.folders.teamBundles || []),
                  ...(caseState.folders.privateBundles || []),
                  ...(caseState.folders.bundles || []),
                  ...(caseState.folders.courtBundles || []),
                ];

                const findFolder = (folders, id) => {
                  for (const folder of folders) {
                    if (folder.id === id) return folder;
                    if (folder.folders && folder.folders.length > 0) {
                      const found = findFolder(folder.folders, id);
                      if (found) return found;
                    }
                  }
                  return null;
                };

                const selectedFolder = findFolder(allFolders, targetFolderId);
                usedFolderName = selectedFolder?.name || 'Unknown folder';
              }

              // Store the folder name for display in async message
              setSelectedFolderName(usedFolderName);

              // Step 3: Proceed with split using the target folder ID
              await dispatch(
                actions.splitDocument({
                  caseId: currentCaseId,
                  fileId: file.id,
                  targetFolderId,
                  splitFiles,
                }),
              );
              // Show async info message instead of closing the modal
              setIsSplitting(false);
              setShowAsyncInfo(true);
            } catch {
              // Error handling is done in the saga
              setIsSplitting(false);
            }
          }),
          saveTitle: T.translate('generic.splitFile'),
          disableSave: !formIsValid || isSplitting,
          loadingBtn: isSplitting,
        })}
        draggableDialog
        fullHeight={!showAsyncInfo}
      >
        {showAsyncInfo ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '40px',
            }}
          >
            <div
              style={{
                textAlign: 'center',
                maxWidth: '600px',
              }}
            >
              <div
                style={{
                  fontSize: '1.1rem',
                  color: '#333',
                  marginBottom: '24px',
                }}
              >
                {T.translate('case.splitAsyncMsg')}
              </div>

              <div
                style={{
                  textAlign: 'left',
                  padding: '20px',
                  border: '1px solid #dee2e6',
                  borderRadius: '8px',
                  backgroundColor: '#f8f9fa',
                }}
              >
                <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: 0 }}>
                  <li style={{ marginBottom: '13px', fontSize: '13px', color: '#495057' }}>
                    {T.translate('case.splitFilesWillAppearInBundle')}:{' '}
                    <strong>{selectedFolderName}</strong>
                  </li>
                  <li style={{ fontSize: '13px', color: '#495057' }}>
                    <div style={{ marginBottom: '4px' }}>
                      {T.translate('case.sourceFileBeingSplit')}:
                    </div>
                    <div style={{ paddingLeft: '16px' }}>
                      <div>
                        <strong>{T.translate('case.splitFileId')}:</strong> {file?.id}
                      </div>
                      <div>
                        <strong>{T.translate('case.splitFileName')}:</strong> {file?.name}
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        ) : (
          <Split
            sizes={[50, 50]}
            minSize={100}
            expandToMin={false}
            gutterSize={10}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            cursor="col-resize"
            className="case-table-and-viewer"
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              {isAnalysing ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  <div
                    style={{
                      width: '75%',
                      padding: '30px 50px',
                      textAlign: 'center',
                      border: '1px solid #333',
                      borderRadius: '5px',
                    }}
                  >
                    <div
                      style={{
                        animation: 'blink 1.5s ease-in-out infinite',
                      }}
                    >
                      <span
                        style={{
                          fontWeight: 600,
                          color: '#333',
                          fontSize: '1.4rem',
                        }}
                      >
                        {T.translate('generic.analysing') || 'Analysing...'}
                      </span>
                    </div>
                    <style>
                      {`
                      @keyframes blink {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.3; }
                      }
                    `}
                    </style>
                    <div
                      style={{
                        marginTop: '20px',
                        padding: '16px 20px',
                        backgroundColor: '#FFF8E1',
                        border: '1px solid #FFB300',
                        borderRadius: '5px',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                      }}
                    >
                      <Info
                        style={{
                          color: '#F57C00',
                          fontSize: '24px',
                          flexShrink: 0,
                          marginTop: '2px',
                        }}
                      />
                      <span
                        style={{
                          color: '#8B6800',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          textAlign: 'left',
                        }}
                      >
                        {T.translate('generic.aiAnalyzerUserInfo')}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div
                    style={{
                      flex: '1 1 auto',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      minHeight: 0,
                      marginBottom: '8px',
                    }}
                    className="custom-scrollbar"
                  >
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                      }}
                      style={{ marginRight: '1rem', marginBottom: '1.5rem' }}
                    >
                      {/* AI Query Accordion */}
                      <Accordion
                        expanded={isAccordionExpanded}
                        onChange={(e, expanded) => setIsAccordionExpanded(expanded)}
                        sx={{ marginBottom: '20px' }}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore />}
                          sx={{
                            backgroundColor: '#F7F7F8',
                            '& .MuiAccordionSummary-content': {
                              margin: '12px 0',
                            },
                          }}
                        >
                          <span style={{ fontWeight: 600 }}>
                            {T.translate('generic.aiQuery') || 'AI Query'}
                          </span>
                        </AccordionSummary>
                        <AccordionDetails
                          sx={{
                            padding: '16px',
                            boxShadow: 'inset 2px 0 4px rgba(0, 0, 0, 0.1)',
                          }}
                        >
                          <div style={{ position: 'relative' }}>
                            <TextField
                              fullWidth
                              multiline
                              rows={12}
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              onKeyDown={handleQueryKeyDown}
                              placeholder={
                                T.translate('generic.enterQuery') || 'Enter your AI query'
                              }
                              variant="outlined"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: 'white',
                                  borderColor: '#EFEFEF',
                                  paddingBottom: '30px',
                                  '& fieldset': {
                                    borderColor: '#EFEFEF',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#EFEFEF',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#EFEFEF',
                                  },
                                  '& textarea': {
                                    '&::-webkit-scrollbar': {
                                      width: '5px',
                                    },
                                    '&::-webkit-scrollbar-track': {
                                      background: 'transparent',
                                    },
                                    '&::-webkit-scrollbar-thumb': {
                                      backgroundColor: '#cdcdcd',
                                      borderRadius: '10px',
                                    },
                                    '&::-webkit-scrollbar-thumb:hover': {
                                      backgroundColor: '#b0b0b0',
                                    },
                                  },
                                },
                              }}
                            />
                            <IconButton
                              onClick={handleSubmitQuery}
                              disabled={!searchQuery || !searchQuery.trim() || isAnalysing}
                              sx={{
                                position: 'absolute',
                                bottom: '5px',
                                right: '5px',
                                padding: '4px',
                                color: '#707070',
                                '&:hover': {
                                  backgroundColor: 'rgba(112, 112, 112, 0.1)',
                                },
                                '&.Mui-disabled': {
                                  color: '#cccccc',
                                },
                              }}
                            >
                              <Send fontSize="small" />
                            </IconButton>
                          </div>
                        </AccordionDetails>
                      </Accordion>

                      {/* Error message when no split files found OR More info and image container */}
                      {noSplitFound ? (
                        <div
                          style={{
                            marginBottom: '16px',
                            marginRight: '2px',
                          }}
                        >
                          <div
                            style={{
                              padding: '16px 20px',
                              backgroundColor: '#FFEBEE',
                              border: '1px solid #EF5350',
                              borderRadius: '5px',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '12px',
                            }}
                          >
                            <Info
                              style={{
                                color: '#C62828',
                                fontSize: '24px',
                                flexShrink: 0,
                                marginTop: '2px',
                              }}
                            />
                            <span
                              style={{
                                color: '#C62828',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                textAlign: 'left',
                              }}
                            >
                              {T.translate('generic.noSplitFoundMessage')}
                            </span>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            marginBottom: '16px',
                            marginRight: '2px',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              gap: '12px',
                            }}
                          >
                            {/* Parent container for checkPages and moreInfo */}
                            <div
                              style={{
                                flex: 1,
                                border: '1px solid #dc3545',
                                borderRadius: '5px',
                                padding: '12px',
                              }}
                            >
                              {/* Check Pages Section */}
                              <div style={{ marginBottom: '12px' }}>
                                <span
                                  style={{
                                    fontSize: '13px',
                                    color: '#dc3545',
                                    fontWeight: 400,
                                  }}
                                >
                                  {T.translate('generic.checkPages')}
                                </span>
                                {!isAnalysing && showAnalysisMessage && (
                                  <div
                                    style={{
                                      position: 'relative',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      color: '#8B6800',
                                      backgroundColor: '#FFF8E1',
                                      padding: '4px 28px 4px 10px',
                                      borderRadius: '4px',
                                      lineHeight: 1.2,
                                      maxWidth: '340px',
                                      marginLeft: '12px',
                                    }}
                                  >
                                    <WarningAmber fontSize="inherit" style={{ marginRight: 6 }} />
                                    <span>{T.translate('generic.splitFileMessage')}</span>
                                    <IconButton
                                      size="small"
                                      aria-label={T.translate('generic.close') || 'Close'}
                                      onClick={() => setShowAnalysisMessage(false)}
                                      sx={{
                                        position: 'absolute',
                                        top: 2,
                                        right: 2,
                                        padding: '2px',
                                        color: '#8B6800',
                                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
                                      }}
                                    >
                                      <Close fontSize="inherit" />
                                    </IconButton>
                                  </div>
                                )}
                              </div>

                              {/* More Info Button and Collapsible Image Section */}
                              <div
                                style={{
                                  border: showPageInfoImage ? '1px solid #dee2e6' : 'none',
                                  borderRadius: '5px',
                                  padding: showPageInfoImage ? '12px' : '0',
                                  backgroundColor: showPageInfoImage ? '#F7F7F8' : 'transparent',
                                  transition: 'all 0.3s ease-in-out',
                                }}
                              >
                                {/* More Info Button */}
                                <div>
                                  <Button
                                    variant="outlined"
                                    onClick={() => setShowPageInfoImage(!showPageInfoImage)}
                                    endIcon={
                                      showPageInfoImage ? (
                                        <ExpandLess fontSize="small" />
                                      ) : (
                                        <ExpandMore fontSize="small" />
                                      )
                                    }
                                    sx={{
                                      borderColor: '#dc3545',
                                      color: '#dc3545',
                                      padding: '4px 24px',
                                      '&:hover': {
                                        borderColor: '#bb2d3b',
                                        backgroundColor: 'rgba(220, 53, 69, 0.04)',
                                      },
                                    }}
                                  >
                                    {showPageInfoImage
                                      ? T.translate('generic.collapse') || 'Collapse'
                                      : T.translate('generic.moreInfo') || 'More info'}
                                  </Button>
                                </div>

                                {/* Collapsible Image Section */}
                                {showPageInfoImage && (
                                  <div
                                    style={{
                                      marginTop: '12px',
                                      transition: 'all 0.3s ease-in-out',
                                    }}
                                  >
                                    <div
                                      style={{
                                        marginBottom: '8px',
                                        fontSize: '12px',
                                        fontStyle: 'italic',
                                        color: '#dc3545',
                                      }}
                                    >
                                      {T.translate('generic.splitMoreInfoText')}
                                    </div>
                                    <img
                                      src="/images/PageInfoWithArrowForSplitFile.png"
                                      alt={T.translate('generic.pageInfo') || 'Page Information'}
                                      loading="lazy"
                                      style={{
                                        width: '100%',
                                        height: 'auto',
                                        display: 'block',
                                        borderRadius: '4px',
                                        cursor: 'zoom-in',
                                      }}
                                      onClick={() => setShowZoomedImage(true)}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Files Accordion */}
                      <div style={{ marginBottom: '15px', marginRight: '2px' }}>
                        <Accordion>
                          <AccordionSummary
                            expandIcon={<ExpandMore />}
                            sx={{
                              backgroundColor: '#F7F7F8',
                              minHeight: '40px',
                              '& .MuiAccordionSummary-content': {
                                margin: '8px 0 15px  0',
                              },
                            }}
                          >
                            <span style={{ fontWeight: 600 }}>
                              {`${fields.length} ${T.translate('generic.filesLabel') || 'files'}`}
                            </span>
                          </AccordionSummary>
                          <AccordionDetails
                            sx={{
                              padding: '16px',
                              boxShadow: 'inset 2px 0 4px rgba(0, 0, 0, 0.1)',
                              maxHeight: '300px',
                              overflowY: 'auto',
                            }}
                            className="custom-scrollbar"
                          >
                            <div style={{ overflowX: 'auto' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                                    <th
                                      style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}
                                    >
                                      {T.translate('case.systemName') || 'File Name'}
                                    </th>
                                    <th
                                      style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}
                                    >
                                      {T.translate('case.startPage') || 'Start Page'}
                                    </th>
                                    <th
                                      style={{ padding: '8px', textAlign: 'left', fontWeight: 600 }}
                                    >
                                      {T.translate('case.endPage') || 'End Page'}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {fields.map((field, index) => (
                                    <tr
                                      key={field.id}
                                      style={{ borderBottom: '1px solid #dee2e6' }}
                                    >
                                      <td style={{ padding: '8px' }}>
                                        {formValues.fileEntries?.[index]?.name || '-'}
                                      </td>
                                      <td style={{ padding: '8px' }}>
                                        {formValues.fileEntries?.[index]?.startPage || '-'}
                                      </td>
                                      <td style={{ padding: '8px' }}>
                                        {formValues.fileEntries?.[index]?.endPage || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </AccordionDetails>
                        </Accordion>
                      </div>

                      {fields.map((field, index) => (
                        <Form.Group
                          key={field.id}
                          style={{
                            padding: '18px 10px 5px 10px',
                            border: '1px solid #3A3A3F26',
                            borderRadius: '5px',
                            marginBottom: '10px',
                            position: 'relative',
                          }}
                        >
                          {fields.length > 1 && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '5px',
                                right: '5px',
                                zIndex: 1,
                              }}
                            >
                              <IconButton
                                onClick={() => {
                                  setDeleteIndex(index);
                                  setDeleteConfirmOpen(true);
                                }}
                                sx={{
                                  color: '#dc3545',
                                  borderRadius: '5px',
                                  padding: '4px',
                                  '&:hover': {
                                    color: '#bb2d3b',
                                  },
                                }}
                              >
                                <Delete />
                              </IconButton>
                            </div>
                          )}

                          {/* Form content */}
                          <Row className="align-items-center">
                            <Col>
                              <TextForm
                                {...register(`fileEntries.${index}.name`, {
                                  required: true,
                                })}
                                label={T.translate('case.systemName')}
                                placeholder={T.translate('case.namePlaceholder')}
                                errors={errors?.fileEntries?.[index]?.name}
                              />
                            </Col>
                          </Row>
                          <Row className="align-items-center">
                            <Col xs={4} md={3}>
                              <TextForm
                                {...register(`fileEntries.${index}.startPage`, {
                                  required: true,
                                  validate: (value) => {
                                    // Allow only positive integers
                                    return (
                                      /^\d+$/.test(value) || T.translate('case.enterValidNumber')
                                    );
                                  },
                                  onChange: (e) => {
                                    const value = e.target.value;
                                    // Only allow digits (no negative values, no text)
                                    if (value !== '' && !/^\d+$/.test(value)) {
                                      e.target.value = value.replace(/\D/g, '');
                                    }
                                  },
                                })}
                                label={T.translate('case.startPage')}
                                placeholder={T.translate('case.startPage')}
                                errors={errors?.fileEntries?.[index]?.startPage}
                                inputMode="numeric"
                              />
                              {initialFileEntries &&
                                initialFileEntries[index] &&
                                initialFileEntries[index].startPage !== '' && (
                                  <div
                                    style={{
                                      marginTop: '2px',
                                      fontSize: '11px',
                                      color: '#666',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {(T.translate('generic.originalValue') || 'Original') + ': '}
                                    {initialFileEntries[index].startPage}
                                  </div>
                                )}
                            </Col>
                            <Col xs={4} md={3}>
                              <TextForm
                                {...register(`fileEntries.${index}.endPage`, {
                                  required: true,
                                  validate: (value) => {
                                    // First validate it's a number
                                    if (!/^\d+$/.test(value)) {
                                      return T.translate('case.enterValidNumber');
                                    }
                                    const startPageInput = parseInt(
                                      formValues.fileEntries?.[index]?.startPage,
                                      10,
                                    );
                                    const endPageInput = parseInt(value, 10);
                                    return (
                                      (!isNaN(startPageInput) &&
                                        !isNaN(endPageInput) &&
                                        endPageInput >= startPageInput) ||
                                      T.translate('case.endPageGreaterThanStart')
                                    );
                                  },
                                  onChange: (e) => {
                                    const value = e.target.value;
                                    // Only allow digits (no negative values, no text)
                                    if (value !== '' && !/^\d+$/.test(value)) {
                                      e.target.value = value.replace(/\D/g, '');
                                    }
                                  },
                                })}
                                label={T.translate('case.endPage')}
                                placeholder={T.translate('case.endPage')}
                                errors={errors?.fileEntries?.[index]?.endPage}
                                inputMode="numeric"
                              />
                              {initialFileEntries &&
                                initialFileEntries[index] &&
                                initialFileEntries[index].endPage !== '' && (
                                  <div
                                    style={{
                                      marginTop: '2px',
                                      fontSize: '11px',
                                      color: '#666',
                                      fontStyle: 'italic',
                                    }}
                                  >
                                    {(T.translate('generic.originalValue') || 'Original') + ': '}
                                    {initialFileEntries[index].endPage}
                                  </div>
                                )}
                            </Col>
                            <Col
                              xs={4}
                              md={6}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                height: '100%',
                              }}
                            >
                              {formValues.fileEntries?.[index]?.startPage &&
                                /^\d+$/.test(formValues.fileEntries[index].startPage) && (
                                  <Button
                                    variant="text"
                                    onClick={() =>
                                      handleGoToPage(
                                        parseInt(formValues.fileEntries[index].startPage, 10),
                                      )
                                    }
                                    sx={{
                                      textTransform: 'none',
                                      color: '#4567e5',
                                      padding: '4px 8px',
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        backgroundColor: 'rgba(69, 103, 229, 0.04)',
                                        textDecoration: 'underline',
                                      },
                                    }}
                                  >
                                    {T.translate('generic.goToPage', {
                                      pageNumber: formValues.fileEntries[index].startPage,
                                    }) || `Go to page ${formValues.fileEntries[index].startPage}`}
                                  </Button>
                                )}
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              {(() => {
                                // eslint-disable-next-line react/prop-types
                                const ErrorMessage = ({ children }) => (
                                  <div
                                    style={{
                                      color: '#dc3545',
                                      fontSize: '0.875em',
                                      marginTop: '0.25rem',
                                    }}
                                  >
                                    {children}
                                  </div>
                                );

                                // Check for negative values first (highest priority)
                                if (
                                  (formValues.fileEntries?.[index]?.startPage &&
                                    parseInt(formValues.fileEntries?.[index]?.startPage, 10) < 1) ||
                                  (formValues.fileEntries?.[index]?.endPage &&
                                    parseInt(formValues.fileEntries?.[index]?.endPage, 10) < 1)
                                ) {
                                  return (
                                    <ErrorMessage>
                                      {T.translate('case.lessThanOnePageNumber')}
                                    </ErrorMessage>
                                  );
                                }

                                // Check if end page exceeds max pages (second priority)
                                if (
                                  formValues.fileEntries?.[index]?.endPage &&
                                  parseInt(formValues.fileEntries?.[index]?.endPage, 10) >
                                    maxDocumentPages
                                ) {
                                  return (
                                    <ErrorMessage>
                                      {T.translate('case.endPageExceedsDocumentPages') ||
                                        `End page cannot exceed ${maxDocumentPages}`}
                                    </ErrorMessage>
                                  );
                                }

                                // Check if start page > end page (third priority)
                                if (
                                  formValues.fileEntries?.[index]?.startPage &&
                                  formValues.fileEntries?.[index]?.endPage &&
                                  parseInt(formValues.fileEntries?.[index]?.startPage, 10) >
                                    parseInt(formValues.fileEntries?.[index]?.endPage, 10)
                                ) {
                                  return (
                                    <ErrorMessage>
                                      {T.translate('case.endPageGreaterThanStart')}
                                    </ErrorMessage>
                                  );
                                }

                                return null;
                              })()}
                            </Col>
                          </Row>

                          {/* Offset From Here Button and Controls */}
                          {/* Only show offset button if there are multiple files and this is not the last file */}
                          {fields.length > 1 && index < fields.length - 1 && (
                            <Row className="mt-2">
                              <Col className="d-flex justify-content-end">
                                {activeOffsetIndex !== index ? (
                                  <Button
                                    variant="text"
                                    onClick={() => setActiveOffsetIndex(index)}
                                    sx={{
                                      textTransform: 'none',
                                      color: '#4567e5',
                                      padding: '4px 8px',
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        backgroundColor: 'rgba(69, 103, 229, 0.04)',
                                      },
                                    }}
                                  >
                                    {T.translate('generic.offsetFromHere') || 'Offset from here'}
                                  </Button>
                                ) : (
                                  <Button
                                    variant="text"
                                    onClick={() => {
                                      setActiveOffsetIndex(null);
                                      setOffsetFromHereValue('');
                                    }}
                                    sx={{
                                      textTransform: 'none',
                                      color: '#dc3545',
                                      padding: '4px 8px',
                                      fontSize: '0.875rem',
                                      '&:hover': {
                                        backgroundColor: 'rgba(220, 53, 69, 0.04)',
                                      },
                                    }}
                                  >
                                    {T.translate('generic.cancel') || 'Cancel'}
                                  </Button>
                                )}
                              </Col>
                            </Row>
                          )}

                          {/* Offset Controls (shown when this file is active) */}
                          {activeOffsetIndex === index && (
                            <div
                              style={{
                                marginTop: '12px',
                                padding: '12px',
                                backgroundColor: '#F7F7F8',
                                border: '1px solid #dee2e6',
                                borderRadius: '5px',
                              }}
                            >
                              <Row className="align-items-center">
                                <Col xs={3}>
                                  <TextForm
                                    label={T.translate('generic.offsetBy') || 'Offset by'}
                                    value={offsetFromHereValue}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Allow empty string, minus sign, or valid numbers (including negative)
                                      if (value === '' || value === '-' || /^-?\d+$/.test(value)) {
                                        setOffsetFromHereValue(value);
                                      }
                                    }}
                                    onKeyDown={(e) => handleOffsetFromHereKeyDown(e, index)}
                                    placeholder={T.translate('generic.enterANumber')}
                                    inputMode="numeric"
                                  />
                                </Col>
                                <Col xs={2} style={{ paddingLeft: '5px' }}>
                                  <Button
                                    variant="contained"
                                    onClick={() => handleApplyOffsetFromHere(index)}
                                    disabled={
                                      isNaN(parseInt(offsetFromHereValue, 10)) ||
                                      offsetFromHereValue === '' ||
                                      offsetFromHereValue === undefined
                                    }
                                    sx={{
                                      bgcolor: '#7BA0F4',
                                      color: 'white',
                                      marginTop: '24px',
                                      '&:hover': {
                                        bgcolor: '#1565c0',
                                      },
                                      '&.Mui-disabled': {
                                        bgcolor: '#cccccc',
                                        color: '#666666',
                                      },
                                    }}
                                  >
                                    {T.translate('generic.apply') || 'Apply'}
                                  </Button>
                                </Col>
                              </Row>
                              <div
                                style={{
                                  marginTop: '10px',
                                  fontSize: '11px',
                                  color: '#555',
                                  lineHeight: 1.3,
                                  fontStyle: 'italic',
                                }}
                              >
                                {T.translate('generic.offsetFromHereInfo')}
                              </div>
                            </div>
                          )}
                        </Form.Group>
                      ))}
                      <Row className="mt-2">
                        <Col className="d-flex justify-content-end">
                          <IconButton
                            size="small"
                            onClick={handleAddFileEntry}
                            sx={{
                              bgcolor: '#7BA0F4',
                              borderRadius: '5px',
                              color: 'white',
                              '&:hover': {
                                bgcolor: '#1565c0',
                              },
                            }}
                          >
                            <span style={{ marginRight: '6px', fontSize: '0.8rem' }}>
                              {T.translate('generic.add').toLocaleUpperCase()}
                            </span>
                            <AddCard fontSize="small" />
                          </IconButton>
                        </Col>
                      </Row>
                    </Form>
                  </div>
                  <div
                    style={{
                      position: 'relative',
                      flex: '0 0 auto',
                      paddingRight: '1rem',
                      width: '100%',
                    }}
                  >
                    <div>
                      <MoveDocument
                        control={control}
                        errors={errors}
                        canUnselect={true}
                        currentFolderType={currentFolder.type}
                        label={T.translate('case.chooseBundleForSplitFiles')}
                        defaultFolderName={autoGeneratedFolderName}
                        excludePublicContainers={true}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div>
              <FileViewer ref={fileViewerRef} fileId={file.id} isPreviewMode copyable />
            </div>
          </Split>
        )}
      </Modal>

      {/* Zoomed Image Dialog */}
      <Dialog
        open={showZoomedImage}
        onClose={() => setShowZoomedImage(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
          },
        }}
      >
        <DialogContent
          sx={{
            position: 'relative',
            padding: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
          }}
        >
          <IconButton
            onClick={() => setShowZoomedImage(false)}
            sx={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
              },
              zIndex: 1,
            }}
          >
            <Close />
          </IconButton>
          <img
            src="/images/PageInfoWithArrowForSplitFile.png"
            alt={T.translate('generic.pageInfo') || 'Page Information'}
            style={{
              maxWidth: '200%',
              maxHeight: '90vh',
              width: 'auto',
              height: 'auto',
              display: 'block',
              borderRadius: '4px',
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          {T.translate('generic.delete') || 'Delete'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            {T.translate('generic.deleteSplitConfirmation')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} color="inherit">
            {T.translate('generic.cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={() => {
              if (deleteIndex !== null) {
                remove(deleteIndex);
              }
              setDeleteConfirmOpen(false);
              setDeleteIndex(null);
            }}
            color="primary"
            variant="contained"
            autoFocus
          >
            {T.translate('generic.confirm') || 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ModalSplit;

ModalSplit.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  selectedRows: PropTypes.array,
};

ModalSplit.defaultProps = {
  selectedRows: [],
};
